import { NOTE_CLASS } from "../ui/card";
import type {
  JournalNpcData,
  JournalEntry,
  NpcDisposition,
  CharacterSheet,
} from "@roll1/shared";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Button } from "../ui/button";
import { X, Plus, ExternalLink } from "lucide-react";
import { useI18n } from "../../i18n";

interface NpcFormProps {
  data: JournalNpcData;
  onChange: (partial: Partial<JournalNpcData>) => void;
  focusedFieldRef: React.MutableRefObject<string | null>;
  entries?: JournalEntry[];
  sheets?: CharacterSheet[];
  characterSheetId?: string;
  onChangeCharacterSheetId?: (sheetId: string | undefined) => void;
  onOpenSheet?: (sheetId: string) => void;
}

const DISPOSITIONS: NpcDisposition[] = [
  "friendly",
  "neutral",
  "hostile",
  "unknown",
];

export default function NpcForm({
  data,
  onChange,
  focusedFieldRef,
  entries,
  sheets,
  characterSheetId,
  onChangeCharacterSheetId,
  onOpenSheet,
}: NpcFormProps) {
  const { t } = useI18n();

  const handleFocus = (field: string) => {
    focusedFieldRef.current = field;
  };
  const handleBlur = () => {
    focusedFieldRef.current = null;
  };

  const addQuestHook = () => {
    onChange({ questHooks: [...data.questHooks, ""] });
  };

  const updateQuestHook = (index: number, value: string) => {
    const updated = [...data.questHooks];
    updated[index] = value;
    onChange({ questHooks: updated });
  };

  const removeQuestHook = (index: number) => {
    onChange({ questHooks: data.questHooks.filter((_, i) => i !== index) });
  };

  return (
    <div className="space-y-3">
      <div>
        <Label className="text-meta text-muted-foreground">
          {t("journal.form.npc.portrait")}
        </Label>
        <Input
          className="h-8 text-ui"
          value={data.portrait ?? ""}
          onChange={(e) => onChange({ portrait: e.target.value })}
          onFocus={() => handleFocus("portrait")}
          onBlur={handleBlur}
        />
      </div>
      <div>
        <Label className="text-meta text-muted-foreground">
          {t("journal.form.npc.race")}
        </Label>
        <Input
          className="h-8 text-ui"
          value={data.race ?? ""}
          onChange={(e) => onChange({ race: e.target.value })}
          onFocus={() => handleFocus("race")}
          onBlur={handleBlur}
        />
      </div>
      <div>
        <Label className="text-meta text-muted-foreground">
          {t("journal.form.npc.occupation")}
        </Label>
        <Input
          className="h-8 text-ui"
          value={data.occupation ?? ""}
          onChange={(e) => onChange({ occupation: e.target.value })}
          onFocus={() => handleFocus("occupation")}
          onBlur={handleBlur}
        />
      </div>
      <div>
        <Label className="text-meta text-muted-foreground">
          {t("journal.form.npc.disposition")}
        </Label>
        <Select
          value={data.disposition}
          onValueChange={(v) => onChange({ disposition: v as NpcDisposition })}
        >
          <SelectTrigger className="h-8 text-ui">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {DISPOSITIONS.map((d) => (
              <SelectItem key={d} value={d}>
                {t(`journal.form.disposition.${d}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-meta text-muted-foreground">
          {t("journal.form.npc.description")}
        </Label>
        <Textarea
          className="text-ui min-h-[80px]"
          value={data.description}
          onChange={(e) => onChange({ description: e.target.value })}
          onFocus={() => handleFocus("description")}
          onBlur={handleBlur}
        />
      </div>
      <div>
        <Label className="text-meta text-muted-foreground">
          {t("journal.form.npc.personality")}
        </Label>
        <Textarea
          className="text-ui min-h-[60px]"
          value={data.personality}
          onChange={(e) => onChange({ personality: e.target.value })}
          onFocus={() => handleFocus("personality")}
          onBlur={handleBlur}
        />
      </div>
      <div className={NOTE_CLASS}>
        <Label className="text-meta text-muted-foreground">
          {t("journal.form.npc.secrets")}
          <span className="ml-1 text-warn text-label">
            {t("journal.editor.gmOnly")}
          </span>
        </Label>
        <Textarea
          className="text-ui min-h-[60px]"
          value={data.secrets}
          onChange={(e) => onChange({ secrets: e.target.value })}
          onFocus={() => handleFocus("secrets")}
          onBlur={handleBlur}
        />
      </div>
      <div className={NOTE_CLASS}>
        <Label className="text-meta text-muted-foreground">
          {t("journal.form.npc.questHooks")}
          <span className="ml-1 text-warn text-label">
            {t("journal.editor.gmOnly")}
          </span>
        </Label>
        {data.questHooks.map((hook, i) => (
          <div key={i} className="flex gap-1 mt-1">
            <Input
              className="h-7 text-ui flex-1"
              value={hook}
              onChange={(e) => updateQuestHook(i, e.target.value)}
              onFocus={() => handleFocus(`questHooks.${i}`)}
              onBlur={handleBlur}
            />
            <Button
              variant="ghost"
              size="sm"
              className="w-7 p-0"
              onClick={() => removeQuestHook(i)}
            >
              <X size={14} />
            </Button>
          </div>
        ))}
        <Button
          variant="ghost"
          size="sm"
          className="mt-1"
          onClick={addQuestHook}
        >
          <Plus size={14} className="mr-1" />
          {t("journal.editor.addItem")}
        </Button>
      </div>
      <div className="flex items-center gap-2">
        <Checkbox
          id="npc-alive"
          checked={data.alive}
          onCheckedChange={(checked) => onChange({ alive: !!checked })}
        />
        <Label htmlFor="npc-alive" className="text-ui">
          {t("journal.form.npc.alive")}
        </Label>
      </div>

      {/* CharacterSheet binding */}
      {sheets && onChangeCharacterSheetId && (
        <div className="pt-3 border-t border-line">
          <Label className="text-meta text-muted-foreground">
            {t("journal.npc.linkedSheet")}
          </Label>
          {characterSheetId ? (
            (() => {
              const sheet = sheets.find((s) => s.id === characterSheetId);
              return (
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-ui truncate flex-1 text-fg">
                    {sheet ? sheet.name : t("journal.npc.sheetDeleted")}
                  </span>
                  {sheet && onOpenSheet && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="shrink-0"
                      onClick={() => onOpenSheet(characterSheetId)}
                    >
                      <ExternalLink size={12} className="mr-1" />
                      {t("journal.npc.openSheet")}
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-7 p-0 shrink-0"
                    onClick={() => onChangeCharacterSheetId(undefined)}
                  >
                    <X size={14} />
                  </Button>
                </div>
              );
            })()
          ) : (
            <Select
              value="__none__"
              onValueChange={(v) => {
                if (v !== "__none__") onChangeCharacterSheetId(v);
              }}
            >
              <SelectTrigger className="h-8 text-ui mt-1">
                <SelectValue placeholder={t("journal.npc.bindSheet")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">{t("common.none")}</SelectItem>
                {sheets.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      )}
    </div>
  );
}
